import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plane } from 'lucide-react';
import { toast } from 'sonner';
import { PTO_TYPE_LABELS } from '@/components/hr/hrUtils';

// Employee: request time off (creates a PTO request the admin approves from the Schedule or HR Portal).
export const TimeOffRequestDialog = ({ open, onClose, date, onSaved }) => {
  const [form, setForm] = useState({ type: 'vacation', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ type: 'vacation', start_date: date || '', end_date: date || '', reason: '' });
  }, [open, date]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error('Pick a start and end date');
    setSaving(true);
    try {
      await hrAPI.createPtoRequest(form);
      toast.success('Time off requested — your manager will review it');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="timeoff-request-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plane className="w-5 h-5 text-violet-600" /> Request time off</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set('type', v)}>
              <SelectTrigger className="mt-1" data-testid="timeoff-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PTO_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From</Label><Input type="date" className="mt-1" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="timeoff-start-input" /></div>
            <div><Label>To</Label><Input type="date" className="mt-1" value={form.end_date} min={form.start_date} onChange={(e) => set('end_date', e.target.value)} data-testid="timeoff-end-input" /></div>
          </div>
          <div><Label>Reason <span className="text-slate-400">(optional)</span></Label><Textarea className="mt-1" rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} data-testid="timeoff-reason-input" /></div>
          <p className="text-xs text-slate-500">Once approved, any shifts on those days are removed from your schedule automatically.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={submit} disabled={saving} data-testid="timeoff-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
